import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Flag, Globe } from "lucide-react";
import { SeasonalEventRegion } from "reedy-data/models";
import { Badge } from "~/components/ui/badge";
import { getRegionLabel } from "./utils";

interface RegionBadgeProps {
  region: SeasonalEventRegion;
  /** Abbreviates the label where the badge has to share a line, e.g. cards. */
  compact?: boolean;
}

export const RegionBadge = ({
  region,
  compact = false,
}: RegionBadgeProps): ReactElement => {
  const { t } = useTranslation(["components"]);
  const isInternational = region === SeasonalEventRegion.INTERNATIONAL;
  const RegionIcon = isInternational ? Globe : Flag;
  const label = getRegionLabel(region, t);
  // Every other region is already a two-letter code.
  const isAbbreviated = compact && isInternational;

  if (!isAbbreviated) {
    return (
      <Badge variant="secondary">
        <RegionIcon aria-hidden />
        {label}
      </Badge>
    );
  }

  // Badge renders a plain span, so the full region has to reach assistive tech
  // as text rather than through an aria-label a generic element may ignore.
  return (
    <Badge variant="secondary">
      <RegionIcon aria-hidden />
      <span aria-hidden>{t("components:regionBadge.internationalShort")}</span>
      <span className="sr-only">{label}</span>
    </Badge>
  );
};
